import React, { useState, useEffect } from 'react';
import { getActivities, getLevels, createActivity, deleteActivity, createLevel, deleteLevel } from '../utils/api';
import { useToast } from './ToastProvider';
import Icon from './Icon';
import '../styles/SettingsManager.css';

function SettingsManager() {
  const [activities, setActivities] = useState({ theoretical: [], practical: [] });
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showLevelModal, setShowLevelModal] = useState(false);
  const [newActivity, setNewActivity] = useState({ type: 'practical', name: '' });
  const [newLevel, setNewLevel] = useState({ name: '', description: '', color: '#22c55e' });
  const toast = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [activitiesData, levelsData] = await Promise.all([
        getActivities(),
        getLevels()
      ]);
      setActivities(activitiesData);
      setLevels(levelsData);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    if (!newActivity.name) {
      toast.error('Veuillez remplir le nom de l\'activité');
      return;
    }
    try {
      await createActivity(newActivity);
      setNewActivity({ type: 'practical', name: '' });
      setShowActivityModal(false);
      loadData();
      toast.success('Activité créée avec succès!');
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      toast.error('Erreur lors de la création de l\'activité');
    }
  };

  const handleDeleteActivity = async (type, activityName) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette activité ?')) {
      try {
        await deleteActivity(`${type}/${encodeURIComponent(activityName)}`);
        loadData();
        toast.success('Activité supprimée avec succès!');
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        toast.error('Erreur lors de la suppression de l\'activité');
      }
    }
  };

  const handleAddLevel = async (e) => {
    e.preventDefault();
    try {
      await createLevel(newLevel);
      setNewLevel({ name: '', description: '', color: '#22c55e' });
      setShowLevelModal(false);
      loadData();
      toast.success('Niveau créé avec succès!');
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      toast.error('Erreur lors de la création du niveau');
    }
  };

  const handleDeleteLevel = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce niveau ?')) {
      try {
        await deleteLevel(id);
        loadData();
        toast.success('Niveau supprimé avec succès!');
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        toast.error('Erreur lors de la suppression du niveau');
      }
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement des paramètres...</p>
      </div>
    );
  }

  return (
    <div className="settings-manager">
      <div className="page-header">
        <h1>Paramètres</h1>
      </div>

      <div className="settings-grid">
        {/* Section Activités */}
        <div className="settings-section full-width">
          <div className="section-header">
            <h2><Icon name="clipboard" /> Activités</h2>
            <button onClick={() => setShowActivityModal(true)} className="btn-primary">
              <Icon name="plus" /> Ajouter
            </button>
          </div>

          <div className="activities-simple-grid">
            <div className="activity-type-group">
              <h3><Icon name="clipboard" /> Activités Théoriques</h3>
              <div className="activities-list">
                {activities.theoretical?.length > 0 ? (
                  activities.theoretical.map((activity, idx) => (
                    <div key={idx} className="activity-chip theoretical">
                      <span>{activity}</span>
                      <button 
                        onClick={() => handleDeleteActivity('theoretical', activity)}
                        className="btn-delete-chip"
                      >
                        <Icon name="close" />
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="empty-activities">Aucune activité</p>
                )}
              </div>
            </div>
            
            <div className="activity-type-group">
              <h3><Icon name="diving" /> Activités Pratiques</h3>
              <div className="activities-list">
                {activities.practical?.length > 0 ? (
                  activities.practical.map((activity, idx) => (
                    <div key={idx} className="activity-chip practical">
                      <span>{activity}</span>
                      <button 
                        onClick={() => handleDeleteActivity('practical', activity)}
                        className="btn-delete-chip"
                      >
                        <Icon name="close" />
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="empty-activities">Aucune activité</p>
                )}
              </div>
            </div>

            <div className="activity-type-group">
              <h3><Icon name="users" /> Activités Communes <span className="label-hint">(tous niveaux)</span></h3>
              <div className="activities-list">
                {activities.common?.length > 0 ? (
                  activities.common.map((activity, idx) => (
                    <div key={idx} className="activity-chip common">
                      <span>{activity}</span>
                      <button 
                        onClick={() => handleDeleteActivity('common', activity)}
                        className="btn-delete-chip"
                      >
                        <Icon name="close" />
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="empty-activities">Aucune activité commune</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Section Niveaux */}
        <div className="settings-section">
          <div className="section-header">
            <h2><Icon name="diving" /> Niveaux</h2>
            <button onClick={() => setShowLevelModal(true)} className="btn-primary">
              <Icon name="plus" /> Ajouter
            </button>
          </div>

          <div className="items-list">
            {levels.length === 0 ? (
              <div className="empty-state">
                <p>Aucun niveau</p>
              </div>
            ) : (
              levels.map((level) => (
                <div key={level.id} className="item-card">
                  <div 
                    className="item-color" 
                    style={{ backgroundColor: level.color }}
                  ></div>
                  <div className="item-info">
                    <h3>{level.name}</h3>
                    <p className="item-description">{level.description}</p>
                  </div>
                  <button 
                    onClick={() => handleDeleteLevel(level.id)}
                    className="btn-delete-item"
                  >
                    <Icon name="trash" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Modal Activité */}
      {showActivityModal && (
        <div className="modal-overlay" onClick={() => setShowActivityModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nouvelle Activité</h2>
              <button onClick={() => setShowActivityModal(false)} className="close-btn"><Icon name="close" /></button>
            </div>

            <form onSubmit={handleAddActivity} className="settings-form">
              <div className="form-group">
                <label>Type *</label>
                <select
                  value={newActivity.type}
                  onChange={(e) => setNewActivity({ ...newActivity, type: e.target.value })}
                  required
                >
                  <option value="practical">Pratique</option>
                  <option value="theoretical">Théorique</option>
                  <option value="common">Commune (tous niveaux)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Nom de l'activité *</label>
                <input
                  type="text"
                  value={newActivity.name}
                  onChange={(e) => setNewActivity({ ...newActivity, name: e.target.value })}
                  required
                  placeholder="Ex: Tables de plongée"
                />
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowActivityModal(false)} className="btn-cancel">
                  Annuler
                </button>
                <button type="submit" className="btn-submit">
                  Créer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Niveau */}
      {showLevelModal && (
        <div className="modal-overlay" onClick={() => setShowLevelModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nouveau Niveau</h2>
              <button onClick={() => setShowLevelModal(false)} className="close-btn"><Icon name="close" /></button>
            </div>

            <form onSubmit={handleAddLevel} className="settings-form">
              <div className="form-group">
                <label>Nom du niveau *</label>
                <input
                  type="text"
                  value={newLevel.name}
                  onChange={(e) => setNewLevel({ ...newLevel, name: e.target.value })}
                  required
                  placeholder="Ex: Niveau 1"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <input
                  type="text"
                  value={newLevel.description}
                  onChange={(e) => setNewLevel({ ...newLevel, description: e.target.value })}
                  placeholder="Ex: Débutant"
                />
              </div>

              <div className="form-group">
                <label>Couleur *</label>
                <div className="color-picker-container">
                  <input
                    type="color"
                    value={newLevel.color}
                    onChange={(e) => setNewLevel({ ...newLevel, color: e.target.value })}
                  />
                  <span className="color-value">{newLevel.color}</span>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowLevelModal(false)} className="btn-cancel">
                  Annuler
                </button>
                <button type="submit" className="btn-submit">
                  Créer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SettingsManager;
